import { isBrowser } from '../platform/env.js';

export interface FileMetadata {
  name: string;
  path: string;
  type: 'file' | 'folder';
  size?: number;
  lastModified?: Date;
  children?: FileMetadata[];
  expanded?: boolean;
  scanStatus?: 'clean' | 'infected' | 'scanning' | 'validating' | 'error' | 'unknown';
}

export interface StorageProvider {
  list(path: string): Promise<FileMetadata[]>;
  read(path: string): Promise<Uint8Array>;
  write(path: string, data: Uint8Array): Promise<void>;
  delete(path: string): Promise<void>;
  getName(): string;
}

export class EnterpriseProvider implements StorageProvider {
  private baseUrl: string;
  private authToken: string;

  constructor(baseUrl: string = 'http://localhost:8000/api/v1', authToken?: string) {
    this.baseUrl = baseUrl;
    this.authToken = authToken || this.getAuthToken();
  }

  getName(): string {
    return 'Enterprise Storage';
  }

  private getAuthToken(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('authToken') || '';
    }
    return '';
  }

  async list(path: string): Promise<FileMetadata[]> {
    try {
      const response = await fetch(`${this.baseUrl}/files/list?path=${encodeURIComponent(path)}`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to list files: ${response.statusText}`);
      }

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('Enterprise storage list error:', error);
      return this.getMockFiles(path);
    }
  }

  async read(path: string): Promise<Uint8Array> {
    try {
      const response = await fetch(`${this.baseUrl}/files/read?path=${encodeURIComponent(path)}`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to read file: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } catch (error) {
      console.error('Enterprise storage read error:', error);
      throw error;
    }
  }

  async write(path: string, data: Uint8Array): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/files/write`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/octet-stream',
        },
        body: JSON.stringify({
          path,
          data: Array.from(data),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to write file: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Enterprise storage write error:', error);
      throw error;
    }
  }

  async delete(path: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/files/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Enterprise storage delete error:', error);
      throw error;
    }
  }

  private getMockFiles(path: string): FileMetadata[] {
    if (path === '/' || path === '') {
      return [
        {
          name: 'src',
          type: 'folder',
          path: '/src',
          children: [
            {
              name: 'components',
              type: 'folder',
              path: '/src/components',
              children: [
                { name: 'Button.tsx', type: 'file', path: '/src/components/Button.tsx', size: 1024 },
                { name: 'Modal.tsx', type: 'file', path: '/src/components/Modal.tsx', size: 2048 },
              ],
            },
            {
              name: 'pages',
              type: 'folder',
              path: '/src/pages',
              children: [
                { name: 'Home.tsx', type: 'file', path: '/src/pages/Home.tsx', size: 1536 },
                { name: 'About.tsx', type: 'file', path: '/src/pages/About.tsx', size: 1200 },
              ],
            },
          ],
        },
        {
          name: 'public',
          type: 'folder',
          path: '/public',
          children: [
            { name: 'index.html', type: 'file', path: '/public/index.html', size: 512 },
            { name: 'favicon.ico', type: 'file', path: '/public/favicon.ico', size: 256 },
          ],
        },
        { name: 'package.json', type: 'file', path: '/package.json', size: 2048 },
        { name: 'README.md', type: 'file', path: '/README.md', size: 1024 },
      ];
    }
    return [];
  }
}

export class DropboxProvider implements StorageProvider {
  private accessToken: string;
  private baseUrl: string = 'https://api.dropboxapi.com/2';

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  getName(): string {
    return 'Dropbox';
  }

  async list(path: string): Promise<FileMetadata[]> {
    try {
      const response = await fetch(`${this.baseUrl}/files/list_folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: path === '/' ? '' : path,
          recursive: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`Dropbox API error: ${response.statusText}`);
      }

      const data = await response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.entries.map((entry: any) => ({
        name: entry.name,
        path: entry.path_lower,
        type: entry['.tag'] === 'folder' ? 'folder' : 'file',
        size: entry.size,
        lastModified: entry.server_modified ? new Date(entry.server_modified) : undefined,
      }));
    } catch (error) {
      console.error('Dropbox storage list error:', error);
      return this.getMockDropboxFiles(path);
    }
  }

  async read(path: string): Promise<Uint8Array> {
    try {
      const response = await fetch('https://content.dropboxapi.com/2/files/download', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Dropbox-API-Arg': JSON.stringify({ path }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download from Dropbox: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } catch (error) {
      console.error('Dropbox storage read error:', error);
      throw error;
    }
  }

  async write(path: string, data: Uint8Array): Promise<void> {
    try {
      const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/octet-stream',
          'Dropbox-API-Arg': JSON.stringify({
            path,
            mode: 'overwrite',
            autorename: false,
          }),
        },
        body: data as any,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload to Dropbox: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Dropbox storage write error:', error);
      throw error;
    }
  }

  async delete(path: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/files/delete_v2`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete from Dropbox: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Dropbox storage delete error:', error);
      throw error;
    }
  }

  private getMockDropboxFiles(path: string): FileMetadata[] {
    if (path === '/' || path === '') {
      return [
        {
          name: 'Documents',
          type: 'folder',
          path: '/documents',
          children: [
            { name: 'project-notes.md', type: 'file', path: '/documents/project-notes.md', size: 2048 },
            { name: 'meeting-minutes.docx', type: 'file', path: '/documents/meeting-minutes.docx', size: 4096 },
          ],
        },
        {
          name: 'Images',
          type: 'folder',
          path: '/images',
          children: [
            { name: 'screenshot.png', type: 'file', path: '/images/screenshot.png', size: 102400 },
            { name: 'logo.svg', type: 'file', path: '/images/logo.svg', size: 8192 },
          ],
        },
        { name: 'backup.zip', type: 'file', path: '/backup.zip', size: 1048576 },
      ];
    }
    return [];
  }
}

export class StorageRegistry {
  private providers = new Map<string, StorageProvider>();
  private static instance: StorageRegistry;

  private constructor() {
    this.initializeDefaultProviders();
  }

  static getInstance(): StorageRegistry {
    if (!StorageRegistry.instance) {
      StorageRegistry.instance = new StorageRegistry();
    }
    return StorageRegistry.instance;
  }

  private async initializeDefaultProviders(): Promise<void> {
    this.register('enterprise', new EnterpriseProvider());
    
    if (!isBrowser) {
      try {
        const { WasabiStorageProvider } = await import('./WasabiStorageProvider.js');
        this.register('wasabi', new WasabiStorageProvider());
      } catch (error) {
        console.warn('[StorageRegistry] WasabiStorageProvider not available in browser');
      }
    }
    
    const dropboxToken = this.getDropboxToken();
    if (dropboxToken) {
      this.register('dropbox', new DropboxProvider(dropboxToken));
    }
  }

  private getDropboxToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dropbox-token');
    }
    return null;
  }

  register(name: string, provider: StorageProvider): void {
    this.providers.set(name, provider);
  }

  getProvider(name: string): StorageProvider | undefined {
    return this.providers.get(name);
  }

  getAvailableProviders(): Array<{ key: string; name: string }> {
    return Array.from(this.providers.entries()).map(([key, provider]) => ({
      key,
      name: provider.getName(),
    }));
  }

  unregister(name: string): boolean {
    return this.providers.delete(name);
  }
}

export const storageRegistry = StorageRegistry.getInstance();
